import { ref, type Ref } from 'vue'

export type RovingOrientation = 'horizontal' | 'vertical' | 'both'

interface UseRovingTabindexOptions {
  /**
   * Which arrow keys activate navigation.
   * @default 'horizontal'
   */
  orientation?: RovingOrientation
  /** Whether focus wraps around from last to first and vice-versa. @default true */
  wrap?: boolean
}

interface UseRovingTabindexReturn {
  /** Index of the currently "roving" item (tabindex 0). */
  activeIndex: Ref<number>
  /** Returns 0 for the active item and -1 for all others. */
  tabindexFor: (index: number) => 0 | -1
  /**
   * Key-down handler to attach to the container element.
   * Moves activeIndex and focuses the target item element.
   */
  onKeydown: (event: KeyboardEvent, items: HTMLElement[]) => void
  /** Call when an item receives focus (e.g. via click) to sync activeIndex. */
  setActive: (index: number) => void
}

/**
 * Implements the roving tabindex pattern for composite widgets
 * (radiogroup, tablist, listbox, etc.).
 *
 * Usage:
 *   const { tabindexFor, onKeydown, setActive } = useRovingTabindex()
 *   // In template: :tabindex="tabindexFor(index)" @keydown="onKeydown($event, itemRefs)"
 */
export function useRovingTabindex(
  options: UseRovingTabindexOptions = {}
): UseRovingTabindexReturn {
  const { orientation = 'horizontal', wrap = true } = options
  const activeIndex = ref(0)

  function tabindexFor(index: number): 0 | -1 {
    return index === activeIndex.value ? 0 : -1
  }

  function setActive(index: number): void {
    activeIndex.value = index
  }

  function moveTo(index: number, items: HTMLElement[]): void {
    if (!items.length) return
    let next = index
    if (wrap) {
      next = ((index % items.length) + items.length) % items.length
    } else {
      next = Math.max(0, Math.min(index, items.length - 1))
    }
    activeIndex.value = next
    items[next]?.focus()
  }

  function onKeydown(event: KeyboardEvent, items: HTMLElement[]): void {
    const total = items.length
    if (!total) return

    const current = activeIndex.value
    const isHoriz = orientation === 'horizontal' || orientation === 'both'
    const isVert = orientation === 'vertical' || orientation === 'both'

    switch (event.key) {
      case 'ArrowRight':
        if (!isHoriz) return
        event.preventDefault()
        moveTo(current + 1, items)
        break
      case 'ArrowLeft':
        if (!isHoriz) return
        event.preventDefault()
        moveTo(current - 1, items)
        break
      case 'ArrowDown':
        if (!isVert) return
        event.preventDefault()
        moveTo(current + 1, items)
        break
      case 'ArrowUp':
        if (!isVert) return
        event.preventDefault()
        moveTo(current - 1, items)
        break
      case 'Home':
        event.preventDefault()
        moveTo(0, items)
        break
      case 'End':
        event.preventDefault()
        moveTo(total - 1, items)
        break
    }
  }

  return { activeIndex, tabindexFor, onKeydown, setActive }
}
